import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  AddressBook,
  MagnifyingGlass,
  DownloadSimple,
  Phone,
  Globe,
  MapPin,
  Clock,
  CaretDown,
  CaretUp,
  X,
  Star,
} from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRESET_LOCATIONS = [
  { label: "All U.S. (50 Cities)", value: "all_us" },
  { label: "Florida", cities: ["Miami FL", "Tampa FL", "Orlando FL", "Jacksonville FL"] },
  { label: "Texas", cities: ["Houston TX", "Dallas TX", "Austin TX", "San Antonio TX", "Fort Worth TX", "El Paso TX"] },
  { label: "California", cities: ["Los Angeles CA", "San Francisco CA", "San Diego CA", "San Jose CA", "Sacramento CA", "Fresno CA"] },
  { label: "New York / NE", cities: ["New York NY", "Boston MA", "Philadelphia PA", "Baltimore MD", "Pittsburgh PA"] },
  { label: "Southeast", cities: ["Atlanta GA", "Charlotte NC", "Nashville TN", "Raleigh NC", "Memphis TN"] },
  { label: "Midwest", cities: ["Chicago IL", "Detroit MI", "Minneapolis MN", "Cleveland OH", "Columbus OH", "Indianapolis IN", "Milwaukee WI", "Kansas City MO", "St Louis MO", "Cincinnati OH"] },
  { label: "West", cities: ["Seattle WA", "Portland OR", "Denver CO", "Phoenix AZ", "Las Vegas NV", "Salt Lake City UT", "Albuquerque NM", "Tucson AZ", "Colorado Springs CO"] },
];

export default function NicheSearch() {
  const [niche, setNiche] = useState("");
  const [customLocations, setCustomLocations] = useState("");
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [limitPerLocation, setLimitPerLocation] = useState(10);
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [searchId, setSearchId] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    axios.get(`${API}/niche-search/history`).then((res) => {
      setHistory(res.data.searches || []);
    }).catch(() => {});
  }, []);

  const togglePreset = (preset) => {
    if (preset.value === "all_us") {
      if (selectedLocations.includes("all_us")) {
        setSelectedLocations([]);
      } else {
        setSelectedLocations(["all_us"]);
      }
      return;
    }
    // Toggle individual cities from a region
    const cities = preset.cities || [];
    const allSelected = cities.every((c) => selectedLocations.includes(c));
    if (allSelected) {
      setSelectedLocations((prev) => prev.filter((l) => !cities.includes(l) && l !== "all_us"));
    } else {
      setSelectedLocations((prev) => {
        const filtered = prev.filter((l) => l !== "all_us");
        return [...new Set([...filtered, ...cities])];
      });
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!niche.trim()) {
      toast.error("Enter a niche / business type");
      return;
    }

    // Build locations list
    let locations = [...selectedLocations];
    if (customLocations.trim()) {
      const custom = customLocations.split("\n").map((l) => l.trim()).filter(Boolean);
      locations = [...locations, ...custom];
    }
    if (locations.length === 0) {
      toast.error("Select or enter at least one location");
      return;
    }

    setSearching(true);
    setResults(null);
    setSearchId(null);

    try {
      const res = await axios.post(`${API}/niche-search`, {
        niche: niche.trim(),
        locations,
        limit_per_location: limitPerLocation,
      });
      setResults(res.data.results || []);
      setSearchId(res.data.id);
      toast.success(`Found ${res.data.count} businesses across ${res.data.locations_searched} locations`);
      // Refresh history
      const histRes = await axios.get(`${API}/niche-search/history`);
      setHistory(histRes.data.searches || []);
    } catch (e) {
      toast.error("Search failed. Try again.");
      console.error(e);
    } finally {
      setSearching(false);
    }
  };

  const downloadCsv = (id) => {
    window.open(`${API}/niche-search/${id}/csv`, "_blank");
  };

  const loadPastSearch = async (id) => {
    try {
      const res = await axios.get(`${API}/niche-search/${id}`);
      setResults(res.data.results || []);
      setSearchId(res.data.id);
      setNiche(res.data.niche || "");
      toast.success(`Loaded ${res.data.results?.length || 0} results`);
    } catch (e) {
      toast.error("Could not load search");
    }
  };

  return (
    <div data-testid="niche-search-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1
            className="text-3xl sm:text-4xl font-black tracking-tighter"
            style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
          >
            Niche Search
          </h1>
          <p className="text-sm text-[#52525B] mt-1">
            Pull business contacts by niche + location. Export to CSV for campaigns.
          </p>
        </div>
        {searchId && (
          <button
            data-testid="download-csv-btn"
            onClick={() => downloadCsv(searchId)}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#002FA7] text-white text-sm font-semibold hover:bg-[#002482] transition-colors"
          >
            <DownloadSimple weight="bold" size={16} />
            Download CSV
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Search Form */}
        <div className="bg-white border border-border p-5 space-y-4">
          <h3 className="text-base font-bold tracking-tight" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
            <AddressBook weight="bold" size={16} className="inline mr-2 text-[#002FA7]" />
            Search Configuration
          </h3>

          <form onSubmit={handleSearch} className="space-y-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#52525B] block mb-1">
                Business Type / Niche
              </label>
              <Input
                data-testid="niche-input"
                value={niche}
                onChange={(e) => setNiche(e.target.value)}
                placeholder="e.g. Dentist, Plumber, Chiropractor, Roofing"
                className="border-border rounded-none focus:border-[#002FA7] focus:ring-0"
              />
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#52525B] block mb-2">
                Locations (Quick Select)
              </label>
              <div className="flex flex-wrap gap-1.5">
                {PRESET_LOCATIONS.map((preset, i) => {
                  const isActive = preset.value === "all_us"
                    ? selectedLocations.includes("all_us")
                    : preset.cities?.every((c) => selectedLocations.includes(c));
                  return (
                    <button
                      key={i}
                      type="button"
                      data-testid={`preset-${i}`}
                      onClick={() => togglePreset(preset)}
                      className={`px-2.5 py-1 text-[11px] font-semibold border transition-colors ${
                        isActive
                          ? "bg-[#002FA7] text-white border-[#002FA7]"
                          : "bg-white text-[#52525B] border-border hover:border-[#002FA7] hover:text-[#002FA7]"
                      }`}
                    >
                      {preset.label}
                    </button>
                  );
                })}
              </div>
              {selectedLocations.length > 0 && !selectedLocations.includes("all_us") && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {selectedLocations.map((loc) => (
                    <span key={loc} className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#EEF2FF] text-[10px] font-medium text-[#002FA7]">
                      {loc}
                      <button type="button" onClick={() => setSelectedLocations((p) => p.filter((l) => l !== loc))}>
                        <X size={8} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              {selectedLocations.includes("all_us") && (
                <p className="mt-2 text-[10px] font-semibold text-[#002FA7]">
                  Searching all 50 major U.S. cities
                </p>
              )}
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#52525B] block mb-1">
                Custom Locations (one per line)
              </label>
              <Textarea
                data-testid="custom-locations"
                value={customLocations}
                onChange={(e) => setCustomLocations(e.target.value)}
                placeholder={"Springfield IL\nBoise ID\nAnchorage AK"}
                rows={3}
                className="border-border rounded-none focus:border-[#002FA7] focus:ring-0 resize-none text-xs"
              />
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#52525B] block mb-1">
                Results per Location
              </label>
              <Input
                data-testid="limit-input"
                type="number"
                min={1}
                max={50}
                value={limitPerLocation}
                onChange={(e) => setLimitPerLocation(Number(e.target.value))}
                className="border-border rounded-none focus:border-[#002FA7] focus:ring-0 w-24"
              />
            </div>

            <button
              data-testid="niche-search-btn"
              type="submit"
              disabled={searching}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#002FA7] text-white text-sm font-semibold hover:bg-[#002482] transition-colors disabled:opacity-50"
            >
              {searching ? (
                <>
                  <div className="spinner" style={{ borderTopColor: "white", borderColor: "rgba(255,255,255,0.3)" }} />
                  Searching...
                </>
              ) : (
                <>
                  <MagnifyingGlass weight="bold" size={16} />
                  Search &amp; Build List
                </>
              )}
            </button>
          </form>

          {/* Search History */}
          {history.length > 0 && (
            <div className="border-t border-border pt-4">
              <button
                data-testid="toggle-history"
                onClick={() => setShowHistory(!showHistory)}
                className="flex items-center justify-between w-full text-xs font-bold uppercase tracking-[0.15em] text-[#52525B]"
              >
                <span className="flex items-center gap-1">
                  <Clock weight="bold" size={12} />
                  Past Searches ({history.length})
                </span>
                {showHistory ? <CaretUp size={12} /> : <CaretDown size={12} />}
              </button>
              {showHistory && (
                <div className="mt-2 space-y-1.5 max-h-48 overflow-y-auto">
                  {history.map((h) => (
                    <div
                      key={h.id}
                      className="flex items-center justify-between p-2 border border-border hover:border-[#002FA7] transition-colors cursor-pointer"
                    >
                      <button
                        data-testid={`load-search-${h.id}`}
                        onClick={() => loadPastSearch(h.id)}
                        className="text-left flex-1"
                      >
                        <div className="text-xs font-semibold truncate">{h.niche}</div>
                        <div className="text-[10px] text-[#52525B]">
                          {h.result_count} results &middot; {new Date(h.created_at).toLocaleDateString()}
                        </div>
                      </button>
                      <button
                        onClick={() => downloadCsv(h.id)}
                        className="p-1 hover:bg-[#EEF2FF] text-[#002FA7]"
                        title="Download CSV"
                      >
                        <DownloadSimple weight="bold" size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Results Table */}
        <div className="lg:col-span-2 bg-white border border-border flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-[#FAFAFA]">
            <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#52525B]">
              {results ? `${results.length} Businesses Found` : "Results"}
            </span>
            {searchId && (
              <button
                data-testid="download-csv-table"
                onClick={() => downloadCsv(searchId)}
                className="flex items-center gap-1 text-xs font-semibold text-[#002FA7] hover:underline"
              >
                <DownloadSimple weight="bold" size={12} />
                CSV
              </button>
            )}
          </div>

          {!results && !searching && (
            <div className="flex-1 flex flex-col items-center justify-center text-[#A1A1AA] py-16">
              <AddressBook weight="thin" size={56} className="mb-3" />
              <p className="text-sm font-medium">Search for businesses by niche</p>
              <p className="text-xs mt-1">Select a niche and locations to build your lead list</p>
            </div>
          )}

          {searching && (
            <div className="flex-1 flex flex-col items-center justify-center py-16">
              <div className="spinner mb-4" style={{ width: 28, height: 28 }} />
              <p className="text-sm font-medium text-[#0A0A0A]">Searching across locations...</p>
              <p className="text-xs text-[#52525B] mt-1">This may take 30-60 seconds for multi-city searches</p>
            </div>
          )}

          {results && results.length === 0 && (
            <div className="flex-1 flex flex-col items-center justify-center text-[#A1A1AA] py-16">
              <p className="text-sm">No results found. Try different keywords or locations.</p>
            </div>
          )}

          {results && results.length > 0 && (
            <div className="overflow-auto flex-1" style={{ maxHeight: "70vh" }}>
              <table className="w-full dense-table">
                <thead className="sticky top-0 z-10">
                  <tr>
                    <th className="text-left">#</th>
                    <th className="text-left">Business Name</th>
                    <th className="text-left">Phone</th>
                    <th className="text-left">Website</th>
                    <th className="text-center">Rating</th>
                    <th className="text-left">Address</th>
                    <th className="text-left">Location</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r, i) => (
                    <tr key={i} className="animate-fade-in-up" style={{ animationDelay: `${Math.min(i * 0.02, 0.5)}s` }}>
                      <td className="text-[#A1A1AA] text-xs">{i + 1}</td>
                      <td className="font-medium text-sm max-w-[200px]">
                        <div className="truncate">{r.business_name || "—"}</div>
                        {r.business_type && (
                          <span className="text-[10px] text-[#52525B]">{r.business_type}</span>
                        )}
                      </td>
                      <td>
                        {r.phones?.length > 0 ? (
                          <div className="space-y-0.5">
                            {r.phones.slice(0, 2).map((p, j) => (
                              <a key={j} href={`tel:${p}`} className="flex items-center gap-1 text-xs font-mono hover:text-[#002FA7]">
                                <Phone weight="bold" size={10} className="text-[#00C853]" />
                                {p}
                              </a>
                            ))}
                          </div>
                        ) : (
                          <span className="text-[#A1A1AA] text-xs">—</span>
                        )}
                      </td>
                      <td className="max-w-[150px] truncate">
                        {r.website ? (
                          <a
                            href={r.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[10px] font-mono text-[#002FA7] hover:underline"
                          >
                            {r.website.replace(/^https?:\/\/(www\.)?/, '').split('/')[0]}
                          </a>
                        ) : <span className="text-[#A1A1AA] text-xs">—</span>}
                      </td>
                      <td className="text-center">
                        {r.rating ? (
                          <div>
                            <span className="text-xs font-bold">{r.rating}</span>
                            <span className="text-[10px] text-[#FFD600] ml-0.5">&#9733;</span>
                            {r.review_count != null && (
                              <div className="text-[10px] text-[#A1A1AA]">({r.review_count})</div>
                            )}
                          </div>
                        ) : (
                          <span className="text-[#A1A1AA] text-xs">—</span>
                        )}
                      </td>
                      <td className="text-xs max-w-[200px] truncate text-[#52525B]">
                        {r.formatted_address || "—"}
                      </td>
                      <td className="text-xs">
                        <span className="flex items-center gap-1">
                          <MapPin weight="bold" size={10} className="text-[#52525B]" />
                          {r.location}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
